import { useLocation, useNavigate } from "react-router-dom";
import { BreadCrumbData } from "@shared/lib/navigation/Breadcrumbs.types";
import { Account } from "@models/account";
import { editLastBread, getLastAvailableLink } from "../utils/breadcrumbs";

type UseAccountTypeNavigationOptions = {
  path: string;
  listBreadcrumb: string;
  structureCrumbs: { search: string; browse: string };
  editLastBreadPath?: string;
  enableDocumentRowSelect?: boolean;
};

/**
 * Navigation helpers for account-type browse pages (structure editor routes).
 */
export function useAccountTypeNavigation({
  path,
  listBreadcrumb,
  structureCrumbs,
  editLastBreadPath,
  enableDocumentRowSelect = false,
}: UseAccountTypeNavigationOptions) {
  const location = useLocation();
  const navigate = useNavigate();

  function rowChosen<T extends Account>(row: T) {
    if (!enableDocumentRowSelect || !location.state?.breadcrumbData) {
      return;
    }

    navigate(getLastAvailableLink(location.state.breadcrumbData), {
      replace: true,
      state: {
        ...location.state,
        document_account: { ...row, id: undefined, account_id: row.id },
        car: null,
        breadcrumbData: location.state.breadcrumbData.slice(0, -1),
      },
    });
  }

  function buildBreadcrumb(state: unknown): BreadCrumbData[] {
    const base: BreadCrumbData[] = [
      { name: "כרטסות" },
      { name: listBreadcrumb, link: path },
    ];

    if (!editLastBreadPath) {
      return base;
    }

    const stateRecord = state as { breadcrumbData?: BreadCrumbData[] } | null | undefined;
    return [...(editLastBread(stateRecord?.breadcrumbData, editLastBreadPath, state) ?? base)];
  }

  function navigateToStructure(kind: "search" | "browse") {
    const currentState = { ...location.state };
    const breadcrumbData = buildBreadcrumb(currentState);
    const crumb = kind === "search" ? structureCrumbs.search : structureCrumbs.browse;

    navigate(`${path}/structure/${kind}`, {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: crumb }],
      },
    });
  }

  return {
    editSearchStructure: () => navigateToStructure("search"),
    editBrowseStructure: () => navigateToStructure("browse"),
    selectRow: enableDocumentRowSelect && Boolean(location.state?.breadcrumbData),
    rowChosen,
  };
}
